import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { useAuth } from '../context/AuthContext'
import { PROD_RELEASE_SCHEDULE } from '../lib/config/release-config'
import {
  RELATED_APP_TO_RELEASE_NOTES_APPLICATION,
  RELEASE_NOTES_APPLICATIONS,
  RELEASE_NOTES_PATCH_TYPES,
  RELEASE_NOTES_STATUSES,
  TASK_STATUS_TO_RELEASE_NOTES_STATUS,
  TASK_TYPE_TO_RELEASE_PATCH_TYPE,
} from '../lib/config/release-notes-config'
import { TASK_STATUS_COLORS, TASK_STATUS_LABELS } from '../lib/config/project-config'
import { canProfileSeeStatus } from '../lib/config/status-config'
import { TaskConcurrencyError, mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { transitionTask, WorkflowAction } from '../lib/workflow/taskWorkflow'
import { sendTaskAssignedEmailIfNeeded } from '../utils/emailNotifications'
import { formatDateTime } from '../utils/formatters'
import { getNextProdReleaseDate } from '../utils/releaseSchedule'

type ReleaseEnvironment = 'uat' | 'prod'
type ReleasesViewMode = 'release' | 'patch_notes'

type ReleaseTaskUpdateResult = {
  taskId: string
  ok: boolean
  reason?: string
}

export const ReleasesPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [activeEnvironment, setActiveEnvironment] = useState<ReleaseEnvironment>('uat')
  const [activeViewMode, setActiveViewMode] = useState<ReleasesViewMode>('release')
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSummary, setActionSummary] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [relatedAppFilter, setRelatedAppFilter] = useState('all')
  const [assigneeFilter, setAssigneeFilter] = useState('all')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [inlineEditTaskId, setInlineEditTaskId] = useState<string | null>(null)
  const [inlinePublishValue, setInlinePublishValue] = useState<'true' | 'false'>('true')

  const isUatReleaseAdmin = Boolean(profile?.is_uat_release_admin)
  const isProdReleaseAdmin = Boolean(profile?.is_prod_release_admin)
  const hasReleaseAccess = isUatReleaseAdmin || isProdReleaseAdmin
  const canSwitchEnvironment = isUatReleaseAdmin && isProdReleaseAdmin

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'releases'],
    queryFn: () => queries.getTasks(false),
    enabled: hasReleaseAccess,
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
    enabled: hasReleaseAccess,
  })

  useEffect(() => {
    if (!canSwitchEnvironment) {
      if (isProdReleaseAdmin) {
        setActiveEnvironment('prod')
      } else if (isUatReleaseAdmin) {
        setActiveEnvironment('uat')
      }
    }
  }, [canSwitchEnvironment, isProdReleaseAdmin, isUatReleaseAdmin])

  const expectedSourceStatus = activeEnvironment === 'uat' ? 'ready_for_uat' : 'ready_for_prod'
  const targetStatus = activeEnvironment === 'uat' ? 'in_uat_testing' : 'completed'
  const profileDisplayById = useMemo(
    () =>
      new Map((profilesQuery.data || []).map((item) => [item.id, item.full_name || item.email || 'Unknown user'])),
    [profilesQuery.data]
  )

  const baseReleaseTasks = useMemo(
    () =>
      (tasksQuery.data || []).filter(
        (task) => task.status === expectedSourceStatus && canProfileSeeStatus(profile, task.status)
      ),
    [tasksQuery.data, expectedSourceStatus, profile]
  )

  const relatedAppFilterOptions = useMemo(
    () =>
      Array.from(new Set(baseReleaseTasks.map((task) => task.related_app || 'Unknown App')))
        .sort((left, right) => left.localeCompare(right)),
    [baseReleaseTasks]
  )

  const assigneeFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          baseReleaseTasks
            .map((task) => task.assigned_to)
            .filter((assignedTo): assignedTo is string => Boolean(assignedTo))
        )
      ),
    [baseReleaseTasks]
  )

  const releaseTasks = useMemo(() => {
    let nextTasks = baseReleaseTasks

    if (relatedAppFilter !== 'all') {
      nextTasks = nextTasks.filter((task) => (task.related_app || 'Unknown App') === relatedAppFilter)
    }

    if (assigneeFilter !== 'all') {
      nextTasks = nextTasks.filter((task) => task.assigned_to === assigneeFilter)
    }

    if (searchQuery.trim()) {
      const normalizedSearch = searchQuery.trim().toLowerCase()
      nextTasks = nextTasks.filter((task) => {
        const assigneeLabel = task.assigned_to ? profileDisplayById.get(task.assigned_to) || '' : ''
        const searchFields = [
          task.title,
          task.related_app || 'Unknown App',
          task.requested_by,
          task.support_ticket_no || '',
          assigneeLabel,
        ]

        return searchFields.some((field) => field.toLowerCase().includes(normalizedSearch))
      })
    }

    return nextTasks
  }, [assigneeFilter, baseReleaseTasks, profileDisplayById, relatedAppFilter, searchQuery])

  const patchNotesTasks = useMemo(() => {
    let nextTasks = (tasksQuery.data || []).filter(
      (task) =>
        task.status === 'ready_for_prod' &&
        task.publish_release_notes &&
        canProfileSeeStatus(profile, task.status)
    )

    if (relatedAppFilter !== 'all') {
      nextTasks = nextTasks.filter((task) => (task.related_app || 'Unknown App') === relatedAppFilter)
    }

    if (assigneeFilter !== 'all') {
      nextTasks = nextTasks.filter((task) => task.assigned_to === assigneeFilter)
    }

    if (searchQuery.trim()) {
      const normalizedSearch = searchQuery.trim().toLowerCase()
      nextTasks = nextTasks.filter((task) => {
        const assigneeLabel = task.assigned_to ? profileDisplayById.get(task.assigned_to) || '' : ''
        const searchFields = [
          task.title,
          task.related_app || 'Unknown App',
          task.requested_by,
          task.support_ticket_no || '',
          assigneeLabel,
        ]

        return searchFields.some((field) => field.toLowerCase().includes(normalizedSearch))
      })
    }

    return nextTasks
  }, [assigneeFilter, profile, profileDisplayById, relatedAppFilter, searchQuery, tasksQuery.data])

  const releaseNotesPatchTypeIndexByKey = useMemo(
    () => new Map(RELEASE_NOTES_PATCH_TYPES.map((value, index) => [value, index])),
    []
  )
  const releaseNotesApplicationIndexByKey = useMemo(
    () => new Map(RELEASE_NOTES_APPLICATIONS.map((value, index) => [value, index])),
    []
  )
  const nextProdReleaseDate = useMemo(() => getNextProdReleaseDate(PROD_RELEASE_SCHEDULE), [])

  const escapeSqlLiteral = (value: string) => value.replace(/'/g, "''")
  const toSqlDateTimeLiteral = (value: Date | string) => {
    const parsed = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return null
    }

    const iso = parsed.toISOString().replace('T', ' ').replace('Z', '')
    return iso.slice(0, 23)
  }

  const patchNotesSql = useMemo(() => {
    const lines = patchNotesTasks
      .map((task) => {
        const releaseNotesStatus = TASK_STATUS_TO_RELEASE_NOTES_STATUS[task.status as keyof typeof TASK_STATUS_TO_RELEASE_NOTES_STATUS]
        const releaseNotesStatusValue = releaseNotesStatus ? RELEASE_NOTES_STATUSES.indexOf(releaseNotesStatus) : -1
        const releasePatchType = TASK_TYPE_TO_RELEASE_PATCH_TYPE[task.type as keyof typeof TASK_TYPE_TO_RELEASE_PATCH_TYPE]
        const patchTypeIndex = releasePatchType
          ? releaseNotesPatchTypeIndexByKey.get(releasePatchType)
          : undefined
        const releaseApplication = RELATED_APP_TO_RELEASE_NOTES_APPLICATION[task.related_app]
        const applicationIndex = releaseApplication
          ? releaseNotesApplicationIndexByKey.get(releaseApplication)
          : undefined

        if (releaseNotesStatusValue < 0 || patchTypeIndex === undefined || applicationIndex === undefined) {
          return `-- Skipped task ${task.id}: unmapped status/app/type (${task.status} / ${task.related_app || 'Unknown App'} / ${task.type})`
        }

        const releaseDate = toSqlDateTimeLiteral(nextProdReleaseDate)
        if (!releaseDate) {
          return `-- Skipped task ${task.id}: unable to calculate next production release date`
        }

        const description = escapeSqlLiteral(task.title || 'Release update')
        const nextReleaseVersion = task.related_app_record?.next_release_version
        if (!nextReleaseVersion) {
          return `-- Skipped task ${task.id}: related app next_release_version is missing`
        }

        const version = escapeSqlLiteral(nextReleaseVersion)

        return `INSERT INTO PatchNotes (Id, ReleaseDate, Status, Application, Type, Description, ReleaseVersion, Details) VALUES (NEWID(), '${releaseDate}', ${releaseNotesStatusValue}, ${applicationIndex}, ${patchTypeIndex}, N'${description}', '${version}', NULL);`
      })
      .filter((line): line is string => Boolean(line))

    return lines.join('\n')
  }, [nextProdReleaseDate, patchNotesTasks, releaseNotesApplicationIndexByKey, releaseNotesPatchTypeIndexByKey])

  useEffect(() => {
    setSelectedTaskIds((previousIds) => {
      const visibleIds = new Set(releaseTasks.map((task) => task.id))
      return previousIds.filter((taskId) => visibleIds.has(taskId))
    })
  }, [releaseTasks])

  const allVisibleSelected =
    releaseTasks.length > 0 && releaseTasks.every((task) => selectedTaskIds.includes(task.id))

  const releaseMutation = useMutation({
    mutationFn: async (taskIds: string[]) => {
      const taskMap = new Map(releaseTasks.map((task) => [task.id, task]))
      const workflowAction: WorkflowAction =
        activeEnvironment === 'uat' ? { type: 'RELEASE_TO_UAT' } : { type: 'RELEASE_TO_PROD' }
      const updates = await Promise.all(
        taskIds.map(async (taskId): Promise<ReleaseTaskUpdateResult> => {
          const task = taskMap.get(taskId)

          if (!task) {
            return { taskId, ok: false, reason: 'Task is no longer available in this release view.' }
          }

          if (task.status !== expectedSourceStatus) {
            return {
              taskId,
              ok: false,
              reason: `Task must be in ${TASK_STATUS_LABELS[expectedSourceStatus]} before release.`,
            }
          }

          const transition = transitionTask(task, workflowAction, profile, {
            users: profilesQuery.data || [],
          })

          if (!transition.ok) {
            return { taskId, ok: false, reason: transition.reason }
          }

          try {
            const updatedTask = await mutations.updateTaskWithExpectedUpdatedAt(task.id, task.updated_at, transition.updates)

            await sendTaskAssignedEmailIfNeeded(updatedTask, profilesQuery.data || [], task.assigned_to, task.status)

            return { taskId, ok: true }
          } catch (error) {
            if (error instanceof TaskConcurrencyError) {
              return { taskId, ok: false, reason: 'Task changed while you were releasing. Refresh and retry.' }
            }

            return {
              taskId,
              ok: false,
              reason: error instanceof Error ? error.message : 'Unexpected update error.',
            }
          }
        })
      )

      const successes = updates.filter((item) => item.ok)
      const failures = updates.filter((item) => !item.ok)

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks', 'kanban'] }),
      ])

      return {
        successes,
        failures,
      }
    },
  })

  const publishNotesMutation = useMutation({
    mutationFn: async ({ taskId, expectedUpdatedAt, publishReleaseNotes }: { taskId: string; expectedUpdatedAt: string; publishReleaseNotes: boolean }) =>
      mutations.updateTaskWithExpectedUpdatedAt(taskId, expectedUpdatedAt, {
        publish_release_notes: publishReleaseNotes,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks', 'kanban'] }),
      ])
    },
  })

  if (!hasReleaseAccess) {
    return <Navigate to="/dashboard" replace />
  }

  const isLoading = tasksQuery.isLoading || profilesQuery.isLoading
  const queryError = tasksQuery.error || profilesQuery.error

  if (isLoading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center">
        <LoadingSpinner size="lg" text="Loading release tasks..." />
      </div>
    )
  }

  if (queryError) {
    return (
      <Card>
        <p className="rounded-lg border border-red-300 bg-red-100 p-4 text-sm text-red-800">
          {queryError instanceof Error ? queryError.message : 'Failed to load release data.'}
        </p>
      </Card>
    )
  }

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds((previousIds) =>
      previousIds.includes(taskId)
        ? previousIds.filter((id) => id !== taskId)
        : [...previousIds, taskId]
    )
  }

  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedTaskIds([])
      return
    }

    setSelectedTaskIds(releaseTasks.map((task) => task.id))
  }

  const performReleaseSelected = async () => {
    if (selectedTaskIds.length === 0) {
      return
    }

    setActionError(null)
    setActionSummary(null)

    try {
      const result = await releaseMutation.mutateAsync(selectedTaskIds)

      const successCount = result.successes.length
      const failureCount = result.failures.length

      if (failureCount > 0) {
        setActionError(
          `${failureCount} item${failureCount === 1 ? '' : 's'} failed. ${result.failures
            .slice(0, 2)
            .map((item) => item.reason)
            .filter(Boolean)
            .join(' ')}`
        )
      }

      if (successCount > 0) {
        setActionSummary(
          `${successCount} item${successCount === 1 ? '' : 's'} updated to ${TASK_STATUS_LABELS[targetStatus]}.`
        )
      }

      setSelectedTaskIds(result.failures.map((item) => item.taskId))
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to complete release action.')
    } finally {
      setIsConfirmOpen(false)
    }
  }

  const handleReleaseSelected = () => {
    if (selectedTaskIds.length === 0 || releaseMutation.isPending) {
      return
    }

    setIsConfirmOpen(true)
  }

  const openInlinePublishEdit = (taskId: string, currentValue: boolean) => {
    setInlineEditTaskId(taskId)
    setInlinePublishValue(currentValue ? 'true' : 'false')
  }

  const closeInlinePublishEdit = () => {
    setInlineEditTaskId(null)
    setInlinePublishValue('true')
  }

  const handleInlinePublishSave = async (taskId: string, expectedUpdatedAt: string) => {
    try {
      setActionError(null)
      await publishNotesMutation.mutateAsync({
        taskId,
        expectedUpdatedAt,
        publishReleaseNotes: inlinePublishValue === 'true',
      })
      closeInlinePublishEdit()
    } catch (error) {
      if (error instanceof TaskConcurrencyError) {
        setActionError('This task changed while you were editing publish notes. Refresh and retry.')
        return
      }

      setActionError(error instanceof Error ? error.message : 'Unable to update publish notes.')
    }
  }

  return (
    <div className="space-y-6">
      <Card
        header={
          <div className="space-y-1">
            <h1 className="text-xl font-semibold text-slate-900">Releases</h1>
            <p className="text-sm text-slate-600">
              Promote tasks that are ready for release into the next release status.
            </p>
          </div>
        }
      >
        {canSwitchEnvironment && (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stone-300 bg-stone-50 p-2">
            <div className="grid w-full grid-cols-2 gap-2 md:w-auto">
              <button
                type="button"
                onClick={() => {
                  setActiveViewMode('release')
                  setActiveEnvironment('uat')
                }}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  activeViewMode === 'release' && activeEnvironment === 'uat'
                    ? 'bg-slate-700 text-stone-100'
                    : 'bg-stone-200 text-slate-700 hover:bg-stone-300'
                }`}
              >
                UAT Releases
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveViewMode('release')
                  setActiveEnvironment('prod')
                }}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  activeViewMode === 'release' && activeEnvironment === 'prod'
                    ? 'bg-slate-700 text-stone-100'
                    : 'bg-stone-200 text-slate-700 hover:bg-stone-300'
                }`}
              >
                Production Releases
              </button>
            </div>

            <button
              type="button"
              onClick={() => setActiveViewMode('patch_notes')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeViewMode === 'patch_notes'
                  ? 'bg-slate-700 text-stone-100'
                  : 'bg-stone-200 text-slate-700 hover:bg-stone-300'
              }`}
            >
              Patch Notes
            </button>
          </div>
        )}

        {!canSwitchEnvironment && (
          <div className="mb-5 flex justify-end rounded-xl border border-stone-300 bg-stone-50 p-2">
            <button
              type="button"
              onClick={() => setActiveViewMode('patch_notes')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeViewMode === 'patch_notes'
                  ? 'bg-slate-700 text-stone-100'
                  : 'bg-stone-200 text-slate-700 hover:bg-stone-300'
              }`}
            >
              Patch Notes
            </button>
          </div>
        )}

        {activeViewMode === 'patch_notes' ? (
          <div className="space-y-4">
            <div className="text-sm text-slate-700">
              Generated SQL for <span className="font-semibold">{patchNotesTasks.length}</span> Production Release
              items (ready_for_prod) where publish notes is enabled.
            </div>
            <textarea
              value={patchNotesSql}
              readOnly
              spellCheck={false}
              className="min-h-[340px] w-full rounded-lg border border-stone-300 bg-stone-50 p-3 font-mono text-xs text-slate-800"
              placeholder="No patch-note SQL rows available for current filters."
            />
          </div>
        ) : (
          <>
            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Search</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Title, ticket, requester, assignee..."
                  className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm text-slate-800"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Related App</span>
                <select
                  value={relatedAppFilter}
                  onChange={(event) => setRelatedAppFilter(event.target.value)}
                  className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm text-slate-800"
                >
                  <option value="all">All Apps</option>
                  {relatedAppFilterOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Assignee</span>
                <select
                  value={assigneeFilter}
                  onChange={(event) => setAssigneeFilter(event.target.value)}
                  className="w-full rounded-lg border border-stone-300 bg-stone-50 px-3 py-2 text-sm text-slate-800"
                >
                  <option value="all">All Assignees</option>
                  {assigneeFilterOptions.map((assigneeId) => (
                    <option key={assigneeId} value={assigneeId}>
                      {profileDisplayById.get(assigneeId) || 'Unknown user'}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-700">
                Showing <span className="font-semibold">{releaseTasks.length}</span> tasks in{' '}
                <span className="font-semibold">{TASK_STATUS_LABELS[expectedSourceStatus]}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={handleToggleSelectAll}>
                  {allVisibleSelected ? 'Clear all' : 'Select all'}
                </Button>
                <Button
                  size="sm"
                  onClick={handleReleaseSelected}
                  isLoading={releaseMutation.isPending}
                  disabled={selectedTaskIds.length === 0}
                >
                  {activeEnvironment === 'uat' ? 'Release to UAT' : 'Release to Prod'} ({selectedTaskIds.length})
                </Button>
              </div>
            </div>

        {actionSummary && (
          <p className="mb-3 rounded-lg border border-emerald-300 bg-emerald-100 p-3 text-sm text-emerald-800">
            {actionSummary}
          </p>
        )}

        {actionError && (
          <p className="mb-3 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-800">{actionError}</p>
        )}

            {releaseTasks.length === 0 ? (
              <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-slate-600">
                No tasks are currently in {TASK_STATUS_LABELS[expectedSourceStatus]}.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-stone-300">
                <table className="min-w-full divide-y divide-stone-300 text-sm">
                  <thead className="bg-stone-200 text-left text-xs uppercase tracking-wide text-slate-700">
                    <tr>
                      <th className="px-3 py-3">Select</th>
                      <th className="px-3 py-3">Title</th>
                      <th className="px-3 py-3">Requested By</th>
                      <th className="px-3 py-3">Related App</th>
                      <th className="px-3 py-3">Next Release</th>
                      <th className="px-3 py-3">Assigned To</th>
                      <th className="px-3 py-3">Publish Notes</th>
                      <th className="px-3 py-3">Updated</th>
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200 bg-stone-100 text-slate-800">
                    {releaseTasks.map((task) => {
                      const isSelected = selectedTaskIds.includes(task.id)
                      return (
                        <tr key={task.id} className={isSelected ? 'bg-blue-100/70' : ''}>
                          <td className="px-3 py-3 align-top">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-stone-400 text-slate-700 focus:ring-slate-500"
                              checked={isSelected}
                              onChange={() => toggleTaskSelection(task.id)}
                              aria-label={`Select ${task.title}`}
                            />
                          </td>
                          <td className="px-3 py-3 align-top">
                            <div className="font-medium text-slate-900">{task.title}</div>
                            {task.support_ticket_no && (
                              <p className="mt-1 text-xs text-slate-600">Ticket: {task.support_ticket_no}</p>
                            )}
                          </td>
                          <td className="px-3 py-3 align-top">{task.requested_by || 'Unknown requester'}</td>
                          <td className="px-3 py-3 align-top">{task.related_app || 'Unknown App'}</td>
                          <td className="px-3 py-3 align-top">{task.related_app_record?.next_release_version || 'n/a'}</td>
                          <td className="px-3 py-3 align-top">
                            {task.assigned_to ? profileDisplayById.get(task.assigned_to) || 'Unknown user' : 'Unassigned'}
                          </td>
                          <td className="px-3 py-3 align-top">
                            {inlineEditTaskId === task.id ? (
                              <div className="flex items-center gap-2">
                                <select
                                  className="rounded-lg border border-stone-300 px-2 py-1 text-sm"
                                  value={inlinePublishValue}
                                  onChange={(event) => setInlinePublishValue(event.target.value as 'true' | 'false')}
                                  autoFocus
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </select>
                                <Button
                                  size="sm"
                                  onClick={() => handleInlinePublishSave(task.id, task.updated_at)}
                                  isLoading={publishNotesMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={closeInlinePublishEdit}
                                  disabled={publishNotesMutation.isPending}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium transition hover:opacity-80 ${
                                  task.publish_release_notes
                                    ? 'border-emerald-500 text-emerald-700'
                                    : 'border-slate-400 text-slate-600'
                                }`}
                                onClick={() => openInlinePublishEdit(task.id, task.publish_release_notes)}
                              >
                                {task.publish_release_notes ? 'Yes' : 'No'}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-3 align-top text-xs text-slate-700">{formatDateTime(task.updated_at)}</td>
                          <td className="px-3 py-3 align-top">
                            <span
                              className="inline-flex rounded-full border px-2 py-0.5 text-xs font-medium"
                              style={{
                                borderColor: TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#64748b',
                                color: TASK_STATUS_COLORS[task.status as keyof typeof TASK_STATUS_COLORS] || '#334155',
                              }}
                            >
                              {TASK_STATUS_LABELS[task.status as keyof typeof TASK_STATUS_LABELS] || task.status}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {isConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-md rounded-2xl border border-stone-400 bg-stone-100 shadow-2xl">
            <div className="border-b border-stone-300 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Confirm Release Update</h2>
              <p className="mt-1 text-sm text-slate-600">
                You are about to move {selectedTaskIds.length} task{selectedTaskIds.length === 1 ? '' : 's'} to{' '}
                {TASK_STATUS_LABELS[targetStatus]}.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsConfirmOpen(false)}
                disabled={releaseMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="button" onClick={performReleaseSelected} isLoading={releaseMutation.isPending}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
